import * as React from 'react';

import type {FilterObject, ComparisonRangeType, TimeseriesReportData} from 'nutshell-core/types';

import {addOffline} from 'shells/hoc/add-offline';

import {ReportProviderContext} from '../report-provider-context';
import type {
    ReportGap,
    ReportFacet,
    DateRangeType,
    ReportSegment,
    ReportKey,
} from '../report-types';

import {ChartEmptyState} from './empty-state/chart-empty-state';
import {ChartErrorState} from './error-state/chart-error-state';
import {LoadingChart} from './loading-chart';
import {SegmentedTimeseriesChart} from './segmented-timeseries-chart';
import {renderTooltip} from './tooltips';

const Chart = addOffline(SegmentedTimeseriesChart);

type Props = {
    dateRangeType: DateRangeType;
    facet: ReportFacet;
    reportGap: ReportGap;
    compareTo?: ComparisonRangeType;
    filters: Array<FilterObject>;
    segment?: ReportSegment;
    // Our deparam library `qs` returns booleans as strings
    useConfidence?: string;
    onClickChartSegment?: (...args: any) => any | null | undefined;
    reportKey: ReportKey;
    height?: number;
    useBuiltInEmptyState?: boolean;
};

export class SegmentedTimeseriesChartContainer extends React.Component<Props> {
    render() {
        const {
            reportGap,
            facet,
            useConfidence,
            compareTo,
            segment,
            dateRangeType,
            reportKey,
            useBuiltInEmptyState = false,
            ...restProps
        } = this.props;

        return (
            <ReportProviderContext.Consumer>
                {/* @ts-expect-error */}
                {({isLoading, isTimedOut, isErrored, chartData}) => {
                    // @ts-expect-error
                    let renderChartTooltip;
                    let reportInfo;
                    if (chartData) {
                        reportInfo = {
                            reports: chartData.reports,
                            referenceReports: chartData.referenceReports,
                        };
                        renderChartTooltip = renderTooltip(
                            dateRangeType,
                            reportInfo,
                            this.props.reportGap,
                            Boolean(segment),
                            useConfidence === 'true',
                            this.props.compareTo,
                            reportKey
                        );
                    }

                    const isRefreshing = chartData && isLoading;

                    if (isLoading && !isRefreshing) {
                        return (
                            <div
                                style={
                                    this.props.height
                                        ? {height: `${this.props.height}px`}
                                        : undefined
                                }
                            >
                                <LoadingChart />
                            </div>
                        );
                    }

                    if (isErrored) {
                        return <ChartErrorState isErrored={true} reportKey={reportKey} />;
                    }

                    if (isTimedOut) {
                        return <ChartErrorState isTimedOut={true} reportKey={reportKey} />;
                    }

                    if (useBuiltInEmptyState && shouldShowBuiltInEmptyState(chartData)) {
                        return (
                            <ChartEmptyState reportKey={this.props.reportKey} isVertical={true} />
                        );
                    }

                    return (
                        <Chart
                            {...restProps}
                            chartData={chartData}
                            isTimeseries={true}
                            isStacked={true}
                            isRefreshing={isRefreshing}
                            reportInfo={reportInfo}
                            reportKey={reportKey}
                            reportParams={{
                                gap: reportGap,
                                facet,
                                dateRangeType,
                                useConfidence,
                                compareTo,
                                segment,
                            }}
                            // @ts-expect-error
                            renderTooltip={(props) => renderChartTooltip(props)}
                            onClickChartSegment={this.props.onClickChartSegment}
                        />
                    );
                }}
            </ReportProviderContext.Consumer>
        );
    }
}

function shouldShowBuiltInEmptyState(chartData: TimeseriesReportData) {
    if (!chartData || !chartData.buckets || chartData.buckets.length === 0) {
        return true;
    }

    return chartData.buckets.every((bucket) => {
        const reportData = bucket && bucket.bucketData && bucket.bucketData.reportData;
        if (!reportData) {
            return true;
        }

        return Object.keys(reportData).every((key) => {
            const report = reportData[key];

            // @ts-expect-error
            return !report.count && !report.total;
        });
    });
}
